/*
 * Pure helpers behind the "total earnings per period" views: AdSense plus
 * Amazon Associates, per site and for the whole portfolio.
 *
 * Kept free of React and router imports so they can be unit-tested directly,
 * like domainMetrics.js.
 *
 * Sources, and what each can answer:
 *   AdSense 30d map   (analytics/adsense_earnings_30d)  the last 30 days only
 *   AdSense daily     (analytics/adsense_earnings_daily) any window inside its
 *                      start..end (about four months), per apex host per day
 *   Amazon reports    (analytics/amazon_earnings)  one per uploaded date range;
 *                      per tracking ID, with per-day rows only when the upload
 *                      was the per-item Earnings report
 *
 * A period one source cannot cover reads as null ("not available"), never as
 * $0, so a total is never silently missing half its money.
 */
import { mstDayWindow, normalizeHost, sumByBaseHost } from './domainMetrics';

export const FALLBACK_TAG = 'developingtheweb';
const TAG_RE = /^[a-z0-9][a-z0-9-]*-2\d$/;

/* Same rule the backend enforces; '' means "remove the tag". */
export const validateAmazonTag = (value) => {
  const tag = String(value || '').trim().toLowerCase();
  if (tag === '' || tag === FALLBACK_TAG || TAG_RE.test(tag)) return { ok: true, value: tag };
  return {
    ok: false,
    value: tag,
    error: `Expected a tracking ID like "mysite-20" (or "${FALLBACK_TAG}")`,
  };
};

/* Inclusive list of YYYY-MM-DD keys from start to end (UTC arithmetic, so no
   DST or local-zone drift). */
export const daysBetween = (start, end) => {
  if (!start || !end || start > end) return [];
  const out = [];
  const d = new Date(`${start}T00:00:00Z`);
  const last = new Date(`${end}T00:00:00Z`);
  if (Number.isNaN(d.getTime()) || Number.isNaN(last.getTime())) return [];
  while (d <= last && out.length < 1000) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* "Sep 1 – 30, 2026", "Sep 15 – Oct 14, 2026", "Dec 1, 2025 – Jan 31, 2026" */
export const formatRange = (start, end) => {
  if (!start || !end) return '';
  const [y1, m1, d1] = start.split('-').map(Number);
  const [y2, m2, d2] = end.split('-').map(Number);
  if (y1 !== y2) return `${MONTHS[m1 - 1]} ${d1}, ${y1} – ${MONTHS[m2 - 1]} ${d2}, ${y2}`;
  if (m1 !== m2) return `${MONTHS[m1 - 1]} ${d1} – ${MONTHS[m2 - 1]} ${d2}, ${y2}`;
  return `${MONTHS[m1 - 1]} ${d1} – ${d2}, ${y2}`;
};

export const ROLLING = [
  { id: 'last30', days: 30, label: 'Last 30 days' },
  { id: 'last7', days: 7, label: 'Last 7 days' },
  { id: 'last90', days: 90, label: 'Last 90 days' },
];

/* The periods the selector offers: the rolling windows, then each uploaded
   Amazon report's exact date range (newest first). */
export const buildPeriods = (amazonPeriods, windowFor = mstDayWindow) => {
  const out = ROLLING.map((r) => {
    const days = windowFor(r.days);
    return { id: r.id, kind: 'rolling', label: r.label, days, start: days[0], end: days[days.length - 1] };
  });
  (amazonPeriods || []).forEach((p) => {
    if (!p || !p.period_start || !p.period_end) return;
    out.push({
      id: `amazon:${p.period_id}`,
      kind: 'amazon',
      periodId: p.period_id,
      label: `${formatRange(p.period_start, p.period_end)} (Amazon report)`,
      start: p.period_start,
      end: p.period_end,
      days: daysBetween(p.period_start, p.period_end),
    });
  });
  return out;
};

/* AdSense earnings per apex host for a period:
   { byHost, available, note }.  available=false means "cannot say". */
export const adsenseForPeriod = (period, { earnings30 = {}, daily = null } = {}) => {
  if (!period) return { byHost: {}, available: false, note: 'No period' };
  if (period.id === 'last30') {
    return { byHost: sumByBaseHost(earnings30), available: true, note: 'AdSense, last 30 days' };
  }
  const map = daily && daily.daily;
  if (!map || !daily.start || !daily.end) {
    return { byHost: {}, available: false,
      note: 'Daily AdSense earnings have not been fetched yet — refresh Earnings' };
  }
  if (period.start < daily.start || period.end > daily.end) {
    return { byHost: {}, available: false,
      note: `AdSense daily earnings only cover ${formatRange(daily.start, daily.end)}` };
  }
  const days = new Set(period.days);
  const byHost = {};
  Object.entries(map).forEach(([host, perDay]) => {
    const base = normalizeHost(host);
    Object.entries(perDay || {}).forEach(([day, value]) => {
      if (!days.has(day)) return;
      byHost[base] = (byHost[base] || 0) + (Number(value) || 0);
    });
  });
  return { byHost, available: true, note: `AdSense, ${formatRange(period.start, period.end)}` };
};

/* Mirror of amazon_reports.attribute(): every tag lands in exactly one bucket. */
export const attributeTags = (byTagEarnings, tags) => {
  const domains = {};
  const unmapped = {};
  const shared = {};
  let fallback = 0;
  Object.entries(byTagEarnings || {}).forEach(([tag, earned]) => {
    const value = Number(earned) || 0;
    const owners = (tags && tags[tag]) || [];
    if (tag === FALLBACK_TAG) fallback += value;
    else if (owners.length === 1) domains[owners[0]] = (domains[owners[0]] || 0) + value;
    else if (owners.length > 1) shared[tag] = { earnings: value, domains: owners };
    else unmapped[tag] = value;
  });
  return { domains, fallback, unmapped, shared };
};

const EMPTY_AMAZON = { domains: {}, fallback: 0, unmapped: {}, shared: {} };

const totalOf = (att) =>
  Object.values(att.domains).reduce((a, v) => a + v, 0)
  + att.fallback
  + Object.values(att.unmapped).reduce((a, v) => a + v, 0)
  + Object.values(att.shared).reduce((a, s) => a + s.earnings, 0);

/* Amazon earnings for a period: { attribution, total, available, note, source }.

   An Amazon report period answers its own dates exactly. A rolling window
   can only be answered from per-day rows (Earnings report uploads), and only
   when every day of the window is covered; overlapping uploads are resolved
   per day by the newest report, so nothing is counted twice. */
export const amazonForPeriod = (period, amazon) => {
  const periods = (amazon && amazon.periods) || [];
  const tags = (amazon && amazon.tags) || {};
  const none = (note) => ({ attribution: EMPTY_AMAZON, total: null, available: false, note, source: null });
  if (!period) return none('No period');
  if (periods.length === 0) return none('No Amazon report uploaded yet');

  if (period.kind === 'amazon') {
    const rec = periods.find((p) => p.period_id === period.periodId);
    if (!rec) return none('That report is no longer stored');
    const byTag = {};
    Object.entries(rec.by_tag || {}).forEach(([t, e]) => { byTag[t] = Number(e && e.earnings) || 0; });
    const attribution = attributeTags(byTag, tags);
    return { attribution, total: totalOf(attribution), available: true,
      note: `Amazon ${rec.report_type === 'earnings' ? 'Earnings' : 'Tracking ID Summary'} report`,
      source: rec };
  }

  // rolling window: needs daily rows for every day
  const ownerOfDay = {};
  periods.forEach((p) => {  // newest first, so the first claim wins
    if (!p.daily) return;
    daysBetween(p.period_start, p.period_end).forEach((d) => {
      if (!(d in ownerOfDay)) ownerOfDay[d] = p;
    });
  });
  const missing = period.days.filter((d) => !(d in ownerOfDay));
  if (missing.length > 0) {
    return none(missing.length === period.days.length
      ? 'No per-day Amazon data for these dates — upload an Earnings report, or pick an Amazon report period'
      : `Per-day Amazon data is missing for ${missing.length} of ${period.days.length} days`);
  }
  const byTag = {};
  period.days.forEach((d) => {
    const rec = ownerOfDay[d];
    Object.entries(rec.daily || {}).forEach(([tag, perDay]) => {
      const v = Number((perDay || {})[d]) || 0;
      if (v) byTag[tag] = (byTag[tag] || 0) + v;
    });
  });
  const attribution = attributeTags(byTag, tags);
  return { attribution, total: totalOf(attribution), available: true,
    note: 'Amazon, from per-day Earnings report rows', source: null };
};

/* Per-domain accessors for the table, plus portfolio totals.
   Each returns null when its source cannot cover the period. */
export const periodEarnings = (domains, adsense, amazon) => {
  const hostOf = (d) => normalizeHost(d.hostname || d.name || '');
  const adsenseOf = (d) => (adsense.available ? (adsense.byHost[hostOf(d)] || 0) : null);
  const amazonOf = (d) => (amazon.available ? (amazon.attribution.domains[d.id] || 0) : null);
  const totalOf2 = (d) => {
    const a = adsenseOf(d);
    const z = amazonOf(d);
    if (a == null && z == null) return null;
    return (a || 0) + (z || 0);
  };
  const sum = (fn) => (domains || []).reduce((acc, d) => acc + (fn(d) || 0), 0);
  const siteAdsense = adsense.available ? sum(adsenseOf) : null;
  const siteAmazon = amazon.available ? sum(amazonOf) : null;
  const att = amazon.attribution;
  const unattributed = amazon.available
    ? att.fallback
      + Object.values(att.unmapped).reduce((a, v) => a + v, 0)
      + Object.values(att.shared).reduce((a, s) => a + s.earnings, 0)
    : null;
  const portfolioAmazon = amazon.available ? siteAmazon + unattributed : null;
  const portfolioTotal = (siteAdsense == null && portfolioAmazon == null)
    ? null : (siteAdsense || 0) + (portfolioAmazon || 0);
  return {
    adsense: adsenseOf,
    amazon: amazonOf,
    total: totalOf2,
    partial: !(adsense.available && amazon.available),
    sites: { adsense: siteAdsense, amazon: siteAmazon, total: sum(totalOf2) },
    unattributed,
    portfolio: { adsense: siteAdsense, amazon: portfolioAmazon, total: portfolioTotal },
  };
};

/* Sites with no amazon_tag of their own: their Amazon links use the fallback. */
export const untaggedDomains = (domains) =>
  (domains || []).filter((d) => !String(d.amazon_tag || '').trim());

/* The newest stored Amazon report whose attribution covers a domain, for the
   page list's site-level line. */
export const latestAmazonForDomain = (amazon, domainId) => {
  const periods = (amazon && amazon.periods) || [];
  if (periods.length === 0) return null;
  const rec = periods[0];
  const att = rec.attribution || { domains: {} };
  return {
    period_start: rec.period_start,
    period_end: rec.period_end,
    earnings: att.domains && domainId in att.domains ? att.domains[domainId] : 0,
  };
};

/* Money that distinguishes "unknown" from zero. */
export const money = (value) => (value == null ? '—' : `$${Number(value).toFixed(2)}`);
